import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationEN from '../locales/en.json';
import translationTR from '../locales/tr.json';

// Define language type
type Language = 'en' | 'tr';

// Context type definition
type LanguageContextType = {
  language: Language;
  changeLanguage: (lang: Language) => void;
};

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: translationEN },
      tr: { translation: translationTR }
    },
    lng: 'tr', // Default language set to Turkish
    fallbackLng: 'tr', // Fallback to Turkish instead of English
    interpolation: {
      escapeValue: false
    }
  });

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Provider component
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('tr'); // Default to Turkish

  // Load saved language preference on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && (savedLang === 'en' || savedLang === 'tr')) {
      setLanguage(savedLang);
      i18n.changeLanguage(savedLang);
    } else {
      // If no saved language, default to Turkish
      setLanguage('tr');
      i18n.changeLanguage('tr');
      localStorage.setItem('language', 'tr');
    }
  }, []);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};